import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

/**
 * Landing page after OAuth redirect.
 *
 * The backend issues a short-lived one-time code and redirects to
 * /auth/callback?code=<uuid>. This page exchanges the code for tokens via a
 * server-side POST (tokens never appear in the browser URL or history).
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const didExchange = useRef(false);

  useEffect(() => {
    if (didExchange.current) return;
    didExchange.current = true;

    const params = new URLSearchParams(window.location.search);
    const error  = params.get('error');
    const code   = params.get('code');

    if (error) {
      navigate('/login?error=' + encodeURIComponent(error), { replace: true });
      return;
    }

    if (!code) {
      navigate('/login?error=auth_failed', { replace: true });
      return;
    }

    // Exchange one-time code for tokens — tokens are returned in JSON, never in the URL
    fetch(`${API_URL}/auth/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
      .then(r => {
        if (!r.ok) throw new Error('exchange failed');
        return r.json();
      })
      .then(({ accessToken, refreshToken }) => {
        if (!accessToken) throw new Error('no token in response');
        localStorage.setItem('access_token', accessToken);
        if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
        // Full page reload so AuthProvider remounts and finds the new token.
        // SPA navigation (navigate()) would keep the existing AuthProvider instance
        // where user=null is already set, causing PrivateRoute to redirect to /login.
        window.location.replace('/runs');
      })
      .catch(() => {
        navigate('/login?error=auth_failed', { replace: true });
      });
  }, [navigate]);

  return (
    <div
      style={{
        minHeight: '100dvh', background: 'var(--c-bg)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: '16px',
      }}
    >
      <div
        style={{
          width: 36, height: 36,
          border: '3px solid var(--c-b4)',
          borderTopColor: 'var(--accent)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <div style={{ fontSize: '14px', color: 'var(--c-t6)' }}>Authenticating…</div>
    </div>
  );
}
